import { useEffect, useState } from "react";
import api, { apiError, fmtUSD, fmtQty } from "@/lib/api";
import { Panel, Table, Empty, StatusBadge, Money } from "@/components/terminal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";

export default function Transactions() {
  const [txs, setTxs] = useState(null);
  const [positions, setPositions] = useState([]);
  const [asset, setAsset] = useState("BTC");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/transactions").then((r) => setTxs(r.data)).catch(() => {});
    api.get("/positions").then((r) => setPositions(r.data.positions)).catch(() => {});
  };
  useEffect(load, []);

  const sell = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/transactions/sell", { asset, amount_usd: parseFloat(amount) });
      toast.success(`Sold. Realized P/L ${r.data.realized_gain >= 0 ? "+" : ""}$${r.data.realized_gain.toFixed(2)}; $${r.data.tax_reserved.toFixed(2)} reserved for taxes`);
      setAmount("");
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  if (!txs) return <div className="text-slate-500 font-mono text-sm">Loading…</div>;
  return (
    <div className="space-y-5" data-testid="transactions-page">
      <h2 className="font-display text-xl font-bold">Transactions</h2>
      <Panel title="Record Sale (generates realized gain/loss, tax event, and tax-reserve funding)" testId="sell-panel">
        {positions.length === 0 ? <Empty>No positions to sell.</Empty> : (
          <form onSubmit={sell} className="flex flex-wrap items-end gap-3">
            <div>
              <div className="metric-label mb-1">Asset</div>
              <Select value={asset} onValueChange={setAsset}>
                <SelectTrigger data-testid="sell-asset-select" className="w-32 bg-[#121316] border-[#2D323E]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#17191E] border-[#2D323E]">
                  {positions.map((p) => <SelectItem key={p.asset} value={p.asset} data-testid={`sell-asset-${p.asset}`}>{p.asset}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="metric-label mb-1">Amount (USD)</div>
              <Input data-testid="sell-amount-input" type="number" min="0.01" step="0.01" required value={amount}
                onChange={(e) => setAmount(e.target.value)} className="w-36 bg-[#121316] border-[#2D323E] font-mono" />
            </div>
            <Button data-testid="sell-submit-button" type="submit" disabled={busy} variant="outline" className="border-[#D4AF37]/50 text-[#D4AF37] font-bold">
              {busy ? "EXECUTING…" : "EXECUTE SALE"}
            </Button>
          </form>
        )}
      </Panel>
      <Panel title="Transaction History" testId="transactions-panel">
        {txs.length === 0 ? <Empty>No transactions yet.</Empty> : (
          <Table testId="transactions-table" head={[{ label: "Time" }, { label: "Type" }, { label: "Asset" }, { label: "Qty", num: true }, { label: "Price", num: true }, { label: "Amount", num: true }, { label: "Realized P/L", num: true }, { label: "Mode" }, { label: "Provider" }, { label: "Status" }]}>
            {txs.map((t) => (
              <tr key={t.id} className="hover:bg-[#222630]" data-testid={`tx-row-${t.id.slice(0, 8)}`}>
                <td className="td-cell font-mono whitespace-nowrap">{new Date(t.timestamp).toLocaleString()}</td>
                <td className={`td-cell font-bold ${t.type === "BUY" ? "text-emerald-400" : "text-red-400"}`}>{t.type}</td>
                <td className="td-cell font-semibold">{t.asset}</td>
                <td className="td-num">{fmtQty(t.quantity)}</td>
                <td className="td-num">{fmtUSD(t.price)}</td>
                <td className="td-num">{fmtUSD(t.amount)}</td>
                <td className="td-num">{t.realized_gain != null ? <Money value={t.realized_gain} tone={t.realized_gain >= 0 ? "pos" : "neg"} /> : "—"}</td>
                <td className="td-cell"><StatusBadge status={t.mode} /></td>
                <td className="td-cell text-slate-500">{t.provider}{t.simulated ? " (sim)" : ""}</td>
                <td className="td-cell"><StatusBadge status={t.status} /></td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
    </div>
  );
}
